import { MediumLargeHeadingStyled } from "src/components/custom/texts/texts.styled";
import { Button, Flex, Input } from "@nubeteck/components";
import { Card, DatePicker, Form, Select, Table, Skeleton } from "antd";
import { ColumnsType } from "antd/es/table";
import React, { useEffect } from "react";
import { getImportDataPage } from "../../../Api/content";
import { useLocale } from "../../../i18n/localeContext";
import { StrapiError } from "../../StrapiError";

function ProjectionsImport({
  isLoading,
  getData,
  data,
  periodosOptions,
  getExcel,
  isLoadingExcel,
}: any) {
  const [pageNumber, setPageNumber] = React.useState(1);
  const [rowsCount, setRowsCount] = React.useState(5);
  const [studentName, setStudentName] = React.useState("");
  const [periodoId, setPeriodoId] = React.useState<number>(0);
  const [anio, setAnio] = React.useState<number>();
  const { locale } = useLocale();
  const [importDataPage, setImportDataPage] = React.useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = React.useState<boolean>(true);
  const [hasError, setHasError] = React.useState(false);

  useEffect(() => {
    let cancel = false;
    setHasError(false);
    setIsLoadingContent(true);
    
    const timeoutId = setTimeout(() => {
      if (!cancel) {
        setImportDataPage(null);
        setIsLoadingContent(false);
        setHasError(locale === 'en');
      }
    }, 3000);
    
    getImportDataPage(locale)
      .then((res) => {
        const data = res?.data?.attributes || res?.data || res;
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(data);
          setIsLoadingContent(false);
        }
      })
      .catch(() => {
        if (!cancel) {
          clearTimeout(timeoutId);
          setImportDataPage(null);
          setIsLoadingContent(false);
          setHasError(locale === 'en');
        }
      });
    return () => {
      cancel = true;
      clearTimeout(timeoutId);
    };
  }, [locale]);

  useEffect(() => {
    getData({
      rowsCount,
      page: pageNumber,
      estudianteNombre: studentName,
      periodoId,
      procesoId: 0,
      anio,
    });
  }, [studentName, rowsCount, pageNumber, getData, periodoId, anio]);

  const columns = React.useMemo<ColumnsType<any>>(
    () => [
      {
        title: importDataPage?.student_proyection_import_table ?.[0]?.label || "Estudiante",
        dataIndex: "estudianteNombre",
      },
      {
        title: importDataPage?.student_proyection_import_table ?.[1]?.label || "Periodo",
        dataIndex: "periodoNombre",
      },
    ],
    [importDataPage],
  );

  if (hasError && locale === 'en') {
    return <StrapiError 
      onRetry={() => {
        setHasError(false);
        setIsLoadingContent(true);
      }}
      onUseDefault={() => {
        setHasError(false);
        setIsLoadingContent(false);
        setImportDataPage(null);
      }}
    />;
  }

  return (
    <>
      {isLoadingContent ? (
        <>
          <Skeleton.Input active size="large" style={{ width: 250, marginBottom: 16 }} />
          <Flex style={{ gap: "15px", alignItems: "center", marginBottom: 16 }}>
            <Skeleton.Input active size="default" style={{ width: 200 }} />
            <Skeleton.Input active size="default" style={{ width: 200 }} />
            <Skeleton.Input active size="default" style={{ width: 200 }} />
            <Skeleton.Input active size="default" style={{ width: 150 }} />
          </Flex>
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Skeleton active paragraph={{ rows: 6 }} />
          </Card>
        </>
      ) : (
        <>
          <MediumLargeHeadingStyled>{importDataPage?.student_proyection_import.title || "Proyecciones de estudiantes importadas"}</MediumLargeHeadingStyled>
          <Flex style={{ gap: "15px", alignItems: "center" }}>
            <Form.Item label={importDataPage?.student_proyection_import.filter || "Filtro por año"}>
              <DatePicker
                picker="year"
                onChange={(e) => {
                  setAnio(e?.toDate().getFullYear());
                }}
                placeholder={importDataPage?.student_proyection_import.year || "Año"}
              />
            </Form.Item>
            <Form.Item label={importDataPage?.student_proyection_import.filter_B || "Filtro por periodo"}>
              <Select
                options={periodosOptions}
                placeholder={importDataPage?.student_proyection_import.select_option || "Seleccione un periodo"}
                onChange={(e) => {
                  setPeriodoId(e);
                }}
                allowClear
              />
            </Form.Item>
            <Form.Item label={importDataPage?.student_proyection_import.filterpage_C || "Filtro por nombre"}>
              <Input
                type="search"
                onChange={(e) => setStudentName(e.target.value)}
                placeholder={importDataPage?.student_proyection_import.search_input || "Búsqueda por nombre de estudiante"}
              />
            </Form.Item>
            <Button
              type="primary"
              onClick={() => getExcel(anio ?? 0, periodoId ?? 0)}
              loading={isLoadingExcel}
            >{`${importDataPage?.student_proyection_import.export_button || "Exportar a excel"} ${
              anio && periodoId > 0
                ? "(año y periodo seleccionado)"
                : anio
                ? "(año seleccionado)"
                : periodoId > 0
                ? "(periodo seleccionado)"
                : ""
            }`}</Button>
          </Flex>
          <Card bodyStyle={{ padding: 0, width: "100%" }}>
            <Table<any>
              loading={isLoading}
              columns={columns}
              dataSource={data?.data?.result}
              rowKey={(v) => v.estudianteId}
              pagination={{
                onChange(page, pageSize) {
                  setPageNumber(page);
                  setRowsCount(pageSize);
                },
                pageSize: rowsCount,
                current: pageNumber,
                total: data?.data?.pageCount * rowsCount,
                showSizeChanger: true,
                style: { marginRight: "30px" },
              }}
              scroll={{ x: 800 }}
            />
          </Card>
        </>
      )}
    </>
  );
}

export default ProjectionsImport;
